import React, { useMemo } from "react";
import { musicClassifierApi } from "../../../../../api/musicClassifierApi";
import { qualityAuditApi } from "../../../../../api/qualityAuditApi";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps, emailsInScope, getScope, matchesEmails } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  ListRows,
  formatNumber,
  formatRelative,
} from "../../ui";
import { plural, toTime, withinLastDays } from "./cardHelpers";

/** Runs list endpoints return full result rows, so keep the page small. */
const RUN_LIMIT = 25;
const RECENT_DAYS = 7;

type RunKind = "music" | "quality";

interface RunSummary {
  kind: RunKind;
  runId: string;
  dateCreated: string | null;
  createdBy: string | null;
  label: string;
  videos: number;
  errors: number;
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" ? (v as Record<string, unknown>) : {};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** Normalise either run type (quality audit runs are untyped on the client). */
const summariseRun = (raw: unknown, kind: RunKind): RunSummary | null => {
  const r = asRecord(raw);
  const runId = str(r.runId);
  if (!runId) return null;
  const results = Array.isArray(r.results) ? r.results : [];
  const total = typeof r.totalResultCount === "number" ? r.totalResultCount : results.length;
  return {
    kind,
    runId,
    dateCreated: str(r.dateCreated),
    createdBy: str(r.createdBy),
    label: str(r.channelTitle) ?? str(r.channelId) ?? (kind === "music" ? "Music classifier run" : "Quality audit"),
    videos: total,
    errors: results.filter((row) => !!asRecord(row).error).length,
  };
};

const KIND_LABEL: Record<RunKind, string> = { music: "Music classifier", quality: "Quality audit" };
const KIND_LINK: Record<RunKind, string> = {
  music: ROUTES.CLASSIFIERS_MUSIC_CLASSIFIER_HISTORY,
  quality: ROUTES.CLASSIFIERS_BULK,
};

const ClassifiersCard: React.FC<WidgetComponentProps> = ({ access, filters }) => {
  const { data, loading, error, reload } = useWidgetData<RunSummary[]>("classifiers", async () => {
    const [music, quality] = await Promise.all([
      musicClassifierApi.listRuns({ limit: RUN_LIMIT }).catch(() => null),
      qualityAuditApi.listRuns({ limit: RUN_LIMIT }).catch(() => null),
    ]);
    if (music === null && quality === null) throw new Error("Could not load classifier runs");
    return [
      ...(music?.runs ?? []).map((r) => summariseRun(r, "music")),
      ...(quality?.runs ?? []).map((r) => summariseRun(r, "quality")),
    ].filter((r): r is RunSummary => r !== null);
  });

  const allowed = emailsInScope(filters, access.email);
  const mine = getScope(filters) === "mine";

  const stats = useMemo(() => {
    const runs = (data ?? [])
      .filter((r) => matchesEmails(allowed, r.createdBy ? [r.createdBy] : []))
      .sort((a, b) => (toTime(b.dateCreated) ?? 0) - (toTime(a.dateCreated) ?? 0));
    const recent = runs.filter((r) => withinLastDays(r.dateCreated, RECENT_DAYS));
    return {
      recentCount: recent.length,
      musicCount: recent.filter((r) => r.kind === "music").length,
      qualityCount: recent.filter((r) => r.kind === "quality").length,
      videos: recent.reduce((sum, r) => sum + r.videos, 0),
      errors: recent.reduce((sum, r) => sum + r.errors, 0),
      latest: runs.slice(0, 3),
      lastRunAt: runs[0]?.dateCreated ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(allowed)]);

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label={`Runs (${RECENT_DAYS}d)`}
          value={stats.recentCount}
          tone={stats.recentCount > 0 ? "info" : "neutral"}
          hint={`${formatNumber(stats.videos)} videos analysed`}
          to={ROUTES.CLASSIFIERS_MUSIC_CLASSIFIER_HISTORY}
          emphasis
        />
        <Kpi
          label="Music classifier"
          value={stats.musicCount}
          hint={`Last ${RECENT_DAYS} days`}
          to={ROUTES.CLASSIFIERS_MUSIC_CLASSIFIER_HISTORY}
        />
        <Kpi
          label="Quality audits"
          value={stats.qualityCount}
          hint={`Last ${RECENT_DAYS} days`}
          to={ROUTES.CLASSIFIERS_BULK}
        />
      </KpiGrid>

      <ListRows
        title={mine ? "Your recent runs" : "Recent runs"}
        emptyText={mine ? "You haven't run a classifier recently" : "No recent classifier runs"}
        rows={stats.latest.map((r) => ({
          key: `${r.kind}:${r.runId}`,
          primary: r.label,
          secondary: `${KIND_LABEL[r.kind]} · ${formatNumber(r.videos)} ${plural(r.videos, "video")}${
            r.errors > 0 ? ` · ${formatNumber(r.errors)} ${plural(r.errors, "error")}` : ""
          } · ${formatRelative(r.dateCreated) ?? "–"}`,
          to: KIND_LINK[r.kind],
        }))}
      />

      <div className="home-card__meta">
        {stats.errors > 0 && (
          <span className="home-text-warn">
            <i className="pi pi-exclamation-circle" /> {formatNumber(stats.errors)} video{" "}
            {plural(stats.errors, "error")} this week
          </span>
        )}
        <Freshness label="Last run" at={stats.lastRunAt} />
      </div>
    </>
  );
};

export default ClassifiersCard;
